import { useState, useEffect, useCallback, useRef } from "react";
import {
  getPropiedadesService,
  createPropiedadService,
  updatePropiedadService,
  updatePropiedadStatusService,
} from "modules/copropiedades/services/propiedades";
import {
  getCommerceId,
  getCommerceName,
  getUserCommerces,
  switchCommerce,
  initializeUserCommerce,
  getCurrentUser,
  hasModulePermission,
} from "modules/copropiedades/services/authService";

export const usePropiedades = () => {
  const [propiedades, setPropiedades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [commerces, setCommerces] = useState([]);
  const [currentCommerceId, setCurrentCommerceId] = useState(null);
  const [currentCommerceName, setCurrentCommerceName] = useState("");
  const [hasPermission, setHasPermission] = useState(false);
  const [permisosPropiedades, setPermisosPropiedades] = useState({
    ver: false,
    crear: false,
    editar: false,
    activar: false,
    desactivar: false,
  });
  const [stats, setStats] = useState({
    total: 0,
    activeCount: 0,
    inactiveCount: 0,
  });

  const isMounted = useRef(true);
  const isLoadingRef = useRef(false);
  const hasLoaded = useRef(false);

  // 🔥 Función reutilizable para verificar permisos
  const checkPermissions = useCallback(() => {
    const canView = hasModulePermission("propiedades", "ver");
    setHasPermission(canView);

    if (canView) {
      const newPermisos = {
        ver: true,
        crear: hasModulePermission("propiedades", "crear"),
        editar: hasModulePermission("propiedades", "editar"),
        activar: hasModulePermission("propiedades", "activar"),
        desactivar: hasModulePermission("propiedades", "desactivar"),
      };

      setPermisosPropiedades(newPermisos);
    } else {
      setPermisosPropiedades({
        ver: false,
        crear: false,
        editar: false,
        activar: false,
        desactivar: false,
      });
    }

    return canView;
  }, []);

  // Inicializar usuario actual y verificar permisos
  useEffect(() => {
    const user = getCurrentUser();
    const commerceId = getCommerceId();
    const commerceName = getCommerceName();
    setCurrentCommerceId(commerceId);
    setCurrentCommerceName(commerceName || "");

    checkPermissions();
  }, [checkPermissions]);

  // Cargar comercios disponibles
  const loadCommerces = useCallback(async () => {
    try {
      const result = await getUserCommerces();
      if (result.success) {
        setCommerces(result.commerces);
        return result.commerces;
      }
      return [];
    } catch (error) {
      console.error("❌ Error al cargar comercios:", error);
      return [];
    }
  }, []);

  // Inicializar commerceId
  const initializeCommerce = useCallback(async () => {
    const existingCommerceId = getCommerceId();

    if (existingCommerceId) {
      const commercesList = await loadCommerces();
      const exists = commercesList.some((c) => c.id === existingCommerceId);
      if (exists) {
        return true;
      }
    }

    const result = await initializeUserCommerce();

    if (result.success) {
      setCurrentCommerceId(result.commerceId);
      setCurrentCommerceName(result.commerceName || "");
      if (result.commerces) {
        setCommerces(result.commerces);
      }
      return true;
    } else {
      console.error("❌ Error al inicializar commerceId:", result.error);
      setError(result.error);
      return false;
    }
  }, [loadCommerces]);

  // Cambiar comercio
  const changeCommerce = useCallback(async (commerceId, commerceName) => {
    const result = switchCommerce(commerceId, commerceName);
    if (result.success) {
      setCurrentCommerceId(commerceId);
      setCurrentCommerceName(commerceName);
      await loadPropiedades();
      return true;
    }
    return false;
  }, []);

  const loadPropiedades = useCallback(async () => {
    if (isLoadingRef.current) return;

    // Verificar permiso antes de cargar
    if (!hasModulePermission("propiedades", "ver")) {
      setError("No tiene permisos para ver propiedades");
      return;
    }

    isLoadingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      const hasCommerce = await initializeCommerce();

      if (!hasCommerce) {
        setError("No se pudo obtener el comercio asociado al usuario");
        setLoading(false);
        isLoadingRef.current = false;
        return;
      }

      const result = await getPropiedadesService();

      if (isMounted.current) {
        if (result.success) {
          setPropiedades(result.data);
          setStats({
            total: result.total,
            activeCount: result.activeCount,
            inactiveCount: result.inactiveCount,
          });
          hasLoaded.current = true;
        } else {
          setError(result.error || "Error al cargar las propiedades");
        }
      }
    } catch (err) {
      if (isMounted.current) {
        setError(err.message || "Error al cargar las propiedades");
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
      isLoadingRef.current = false;
    }
  }, [initializeCommerce]);

  const addPropiedad = useCallback(async (newPropiedad) => {
    if (!hasModulePermission("propiedades", "crear")) {
      return { success: false, error: "No tiene permisos para crear propiedades" };
    }

    try {
      const result = await createPropiedadService(newPropiedad);

      if (result.success) {
        setPropiedades((prev) => {
          const updated = [...prev, result.data];
          setStats({
            total: updated.length,
            activeCount: updated.filter((p) => p.status === 1).length,
            inactiveCount: updated.filter((p) => p.status === 0).length,
          });
          return updated;
        });
        return { success: true, data: result.data };
      } else {
        return { success: false, error: result.error };
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, []);

  const editPropiedad = useCallback(async (updatedPropiedad) => {
    if (!hasModulePermission("propiedades", "editar")) {
      return { success: false, error: "No tiene permisos para editar propiedades" };
    }

    try {
      const result = await updatePropiedadService(updatedPropiedad);

      if (result.success) {
        setPropiedades((prev) => {
          const updated = prev.map((p) => (p.id === updatedPropiedad.id ? updatedPropiedad : p));
          setStats({
            total: updated.length,
            activeCount: updated.filter((p) => p.status === 1).length,
            inactiveCount: updated.filter((p) => p.status === 0).length,
          });
          return updated;
        });
        return { success: true };
      } else {
        return { success: false, error: result.error };
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, []);

  const togglePropiedadStatus = useCallback(async (id, currentStatus) => {
    if (
      !hasModulePermission("propiedades", "activar") &&
      !hasModulePermission("propiedades", "desactivar")
    ) {
      return { success: false, error: "No tiene permisos para cambiar el estado de propiedades" };
    }

    try {
      const newStatus = currentStatus === 1 ? 0 : 1;
      const result = await updatePropiedadStatusService(id, newStatus);

      if (result.success) {
        setPropiedades((prev) => {
          const updated = prev.map((p) => {
            if (p.id === id) {
              return { ...p, status: newStatus };
            }
            return p;
          });
          setStats({
            total: updated.length,
            activeCount: updated.filter((p) => p.status === 1).length,
            inactiveCount: updated.filter((p) => p.status === 0).length,
          });
          return updated;
        });
        return {
          success: true,
          message:
            newStatus === 1
              ? "Propiedad activada exitosamente"
              : "Propiedad desactivada exitosamente",
          newStatus,
        };
      } else {
        return { success: false, error: result.error };
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, []);

  const refreshData = useCallback(async () => {
    hasLoaded.current = false;
    await loadPropiedades();
  }, [loadPropiedades]);

  // 🔥 Escuchar el evento "permissionsUpdated"
  useEffect(() => {
    const handlePermissionsUpdate = async () => {
      const canView = checkPermissions();

      if (canView) {
        hasLoaded.current = false;
        await loadPropiedades();
        await loadCommerces();
      } else {
        setPropiedades([]);
        setStats({ total: 0, activeCount: 0, inactiveCount: 0 });
      }
    };

    window.addEventListener("permissionsUpdated", handlePermissionsUpdate);

    return () => {
      window.removeEventListener("permissionsUpdated", handlePermissionsUpdate);
    };
  }, [checkPermissions, loadPropiedades, loadCommerces]);

  useEffect(() => {
    if (!hasLoaded.current) {
      loadPropiedades();
      loadCommerces();
    }

    return () => {
      isMounted.current = false;
    };
  }, [loadPropiedades, loadCommerces]);

  return {
    propiedades,
    loading,
    error,
    stats,
    commerces,
    currentCommerceId,
    currentCommerceName,
    hasPermission,
    permisosPropiedades,
    addPropiedad,
    editPropiedad,
    togglePropiedadStatus,
    refreshData,
    changeCommerce,
    hasData: propiedades.length > 0,
  };
};
